import { useDialogFocus } from "./Dialog";
import React, { useState, useRef } from "react";
import { X, Zap, Crown } from "lucide-react";
import { authHeaders } from "../firebase/authHeaders";

interface BuyCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  plusCredits: number;
  proCredits: number;
  uid: string;
}

interface PriceOption {
  priceId: string;
  tier: "plus" | "pro";
  quantity: number;
  price: string;
  perDebate: string;
  popular?: boolean;
}

const priceOptions: PriceOption[] = [
  {
    priceId: "price_1TCmU1A409CfsIzFiVmMSKLX",
    tier: "plus",
    quantity: 15,
    price: "$4.99",
    perDebate: "$0.33",
  },
  {
    priceId: "price_1TCmUHA409CfsIzFlsR1gR7E",
    tier: "plus",
    quantity: 40,
    price: "$9.99",
    perDebate: "$0.25",
    popular: true,
  },
  {
    priceId: "price_1TCRsGA409CfsIzFVSkk2eoF",
    tier: "pro",
    quantity: 5,
    price: "$7.99",
    perDebate: "$1.60",
  },
  {
    priceId: "price_1TCRsGA409CfsIzFsCB60dQh",
    tier: "pro",
    quantity: 15,
    price: "$19.99",
    perDebate: "$1.33",
    popular: true,
  },
];

const BuyCreditsModal: React.FC<BuyCreditsModalProps> = ({
  isOpen,
  onClose,
  plusCredits,
  proCredits,
  uid,
}) => {
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus(dialogRef, isOpen, onClose);

  if (!isOpen) return null;

  const handlePurchase = async (option: PriceOption) => {
    setLoading(option.priceId);
    setError(null);
    try {
      const response = await fetch(
        "https://createcheckoutsession-mt62q3wpaa-uc.a.run.app/createCheckoutSession",
        {
          method: "POST",
          headers: await authHeaders(),
          body: JSON.stringify({
            priceId: option.priceId,
            uid,
          }),
        },
      );
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to create checkout session");
      }
      const { url } = await response.json();
      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (err: any) {
      console.error("Checkout error:", err);
      setError(err.message || "Something went wrong. Please try again.");
      setLoading(null);
    }
  };

  const plusOptions = priceOptions.filter((o) => o.tier === "plus");
  const proOptions = priceOptions.filter((o) => o.tier === "pro");

  return (
    <div className="motion-fade fixed inset-0 z-[100] flex items-center justify-center bg-overlay/50 backdrop-blur-sm p-4">
      <div ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Debate credits" className="motion-dialog bg-surface rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-2">
          <div>
            <h2 className="text-xl font-semibold text-content">
              Buy Debate Credits
            </h2>
            <p className="text-sm text-muted mt-1">
              Power up your AI debates with premium models
            </p>
          </div>
          <button
            onClick={onClose} aria-label="Close credits"
            className="p-2 hover:bg-raised rounded-md transition-colors"
          >
            <X size={20} className="text-muted" />
          </button>
        </div>

        {/* Current balance */}
        <div className="mx-6 mb-4 flex gap-3">
          <div className="flex-1 bg-accent-soft border border-accent-line rounded-xl p-3 text-center">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Zap size={14} className="text-accent" />
              <span className="text-xs font-semibold text-accent uppercase tracking-wide">
                Plus
              </span>
            </div>
            <div className="text-2xl font-semibold text-accent">
              {plusCredits}
            </div>
            <div className="text-xs text-accent">debates left</div>
          </div>
          <div className="flex-1 bg-accent-soft border border-accent-line rounded-xl p-3 text-center">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Crown size={14} className="text-accent" />
              <span className="text-xs font-semibold text-accent uppercase tracking-wide">
                Pro
              </span>
            </div>
            <div className="text-2xl font-semibold text-accent">
              {proCredits}
            </div>
            <div className="text-xs text-accent">debates left</div>
          </div>
        </div>

        {error && (
          <div className="mx-6 mb-3 bg-danger-soft border border-danger-line text-danger text-sm px-4 py-2.5 rounded-lg">
            {error}
          </div>
        )}

        {/* Plus tier */}
        <div className="px-6 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Zap size={16} className="text-accent" />
            <h3 className="font-semibold text-content">Plus Debates</h3>
            <span className="text-xs text-muted">
              Fast models from several labs, updated daily
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {plusOptions.map((option) => (
              <button
                key={option.priceId}
                onClick={() => handlePurchase(option)}
                disabled={loading !== null}
                className={`relative p-4 rounded-xl border-2 text-left transition-all hover:shadow-md ${
                  option.popular
                    ? "border-accent-line bg-accent-soft/50 hover:border-accent-line"
                    : "border-line bg-surface hover:border-accent-line"
                } ${loading === option.priceId ? "opacity-70" : ""} ${loading !== null && loading !== option.priceId ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {option.popular && (
                  <span className="absolute -top-2.5 left-3 bg-action text-on-solid text-xs font-semibold px-2 py-0.5 rounded-md uppercase tracking-wide">
                    Best Value
                  </span>
                )}
                <div className="text-lg font-semibold text-content">
                  {option.quantity} debates
                </div>
                <div className="text-xl font-semibold text-accent mt-1">
                  {option.price}
                </div>
                <div className="text-xs text-muted mt-0.5">
                  {option.perDebate}/debate
                </div>
                {loading === option.priceId && (
                  <div className="mt-2 text-xs text-accent animate-pulse">
                    Redirecting to checkout…
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Pro tier */}
        <div className="px-6 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Crown size={16} className="text-accent" />
            <h3 className="font-semibold text-content">Pro Debates</h3>
            <span className="text-xs text-muted">
              Top-scoring models from several labs, updated daily
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {proOptions.map((option) => (
              <button
                key={option.priceId}
                onClick={() => handlePurchase(option)}
                disabled={loading !== null}
                className={`relative p-4 rounded-xl border-2 text-left transition-all hover:shadow-md ${
                  option.popular
                    ? "border-accent-line bg-accent-soft/50 hover:border-accent-line"
                    : "border-line bg-surface hover:border-accent-line"
                } ${loading === option.priceId ? "opacity-70" : ""} ${loading !== null && loading !== option.priceId ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {option.popular && (
                  <span className="absolute -top-2.5 left-3 bg-action text-on-solid text-xs font-semibold px-2 py-0.5 rounded-md uppercase tracking-wide">
                    Best Value
                  </span>
                )}
                <div className="text-lg font-semibold text-content">
                  {option.quantity} debates
                </div>
                <div className="text-xl font-semibold text-accent mt-1">
                  {option.price}
                </div>
                <div className="text-xs text-muted mt-0.5">
                  {option.perDebate}/debate
                </div>
                {loading === option.priceId && (
                  <div className="mt-2 text-xs text-accent animate-pulse">
                    Redirecting to checkout…
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6">
          <div className="flex items-center justify-center gap-1.5 text-xs text-muted">
            <Zap size={12} />
            <span>Secure checkout powered by Stripe</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyCreditsModal;
